import * as React from 'react'
import {
	StyledCircularProgress,
	StyledCircle,
	StyledPath
} from './CircularProgress.style'
import { themeGet } from '../../utils/index'


type Props = {
	bgColor?: string
	height: number
	start?: 'top' | 'bottom' | 'left' | 'right'
	startAngle?: number
	endAngle?: number
	strokeWidth?: number
	strokeInset?: number
	theme?: any
	value: number
	valueColor?: string | string[]
	width: number
	anchor?: string,
	animation?: any
}

const angleMap = {
	'top': 0,
	'bottom': 180,
	'left': 270,
	'right': 90
}

const polarToCartesian = (x: number, y: number, radius: number, angle: number): { x: number, y: number } => {
	const radians = ((angle - 90) * Math.PI) / 180
	return {
		x: x + radius * Math.cos(radians),
		y: y + radius * Math.sin(radians)
	}
}

const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number): string => {
	let normalizedEndAngle = endAngle
	if (endAngle - startAngle >= 360) {
		normalizedEndAngle = startAngle + 359.99
	}

	const start = polarToCartesian(x, y, radius, startAngle)
	const end = polarToCartesian(x, y, radius, normalizedEndAngle)
	const arcSweep = normalizedEndAngle - startAngle <= 180 ? '0' : '1'
	const clockwise = true

	return [
		'M', start.x.toFixed(10), start.y.toFixed(10),
		'A', radius.toFixed(10), radius.toFixed(10),
		0, arcSweep, (clockwise ? 1 : 0),
		end.x.toFixed(10), end.y.toFixed(10)
	].join(' ')
}

const getDashValue = (radius: number, progress: number): number => {
	const c = 2 * Math.PI * radius
	return c * progress
}

const CircularProgress: React.FC<Props> = (props: any): React.ReactElement => {
	const {
		bgColor,
		height,
		start,
		startAngle,
		endAngle,
		strokeWidth,
		strokeInset,
		value,
		valueColor,
		width,
		animation,
		...rest
	} = props

	const radius = Math.round((Math.min(width, height) - strokeWidth) / 2) - 4
	const clampedValue = Math.max(0, Math.min(1, value))
	const dashArray = radius * Math.PI * 2
	const dashOffset = dashArray - dashArray * clampedValue

	const starting = startAngle !== undefined ? startAngle : 0
	const ending = endAngle !== undefined ? endAngle : 360
	const valueEnding = (Math.abs(starting) + Math.abs(ending)) * clampedValue + starting
	const valueProgress = (valueEnding - starting) / (ending - starting)
	const rotation = `rotate(${angleMap[start] || 0}deg)`

	const center = { x: width / 2, y: height / 2 }
	const outerPath = describeArc(center.x, center.y, radius, starting, ending)
	const innerPath = describeArc(center.x, center.y, radius, starting, ending)

	// animation config
	let initialColor = themeGet(`colors.${valueColor}`, valueColor)(rest)
	let colors: string[] = null
	let times: number[] = null

	if (valueColor instanceof Array) {
		colors = valueColor
		initialColor = colors[0]
		times = []

		const lastIndex = colors.length - 1
		colors.forEach((color, index) => times.push(index / lastIndex))
	}

	return (
		<StyledCircularProgress width={width} height={height} {...rest}>
			<g style={{
				transform: rotation,
				transformOrigin: '50% 50%'
			}}>
				<StyledPath
					d={outerPath}
					strokeWidth={strokeWidth}
					stroke={themeGet(`colors.${bgColor}`, bgColor)(rest)}
				/>
				<StyledPath
					d={innerPath}
					strokeWidth={strokeWidth - strokeInset * 2}
					stroke={initialColor}
					style={{ pathLength: 0 }}
					initial={animation === false ? false : {
						pathLength: 0,
						stroke: initialColor
					}}
					animate={{
						pathLength: valueProgress,
						stroke: colors || initialColor
					}}
					transition={{
						type: 'spring',
						damping: 50,
						stiffness: 50,
						times,
						...animation
					}}
				/>
			</g>
		</StyledCircularProgress>
	)
}

CircularProgress.defaultProps = {
	bgColor: '#ddd',
	height: 100,
	start: 'top',
	strokeWidth: 10,
	strokeInset: 2,
	value: 0,
	valueColor: '#333',
	width: 100
}

export { CircularProgress }
